/**
 * Execution Engine — validação de conclusão, regras e visibilidade
 * 
 * Responsável por:
 * - Validar se uma tarefa pode ser concluída (campos, checklist, anexos, regras)
 * - Avaliar regras condicionais customizadas
 * - Calcular campos visíveis com base em visibilidade condicional
 */

import type { ExecutionConfig, ExecutionField, StageRule, FieldVisibilityRule, ExecutionBlock, FieldBlockConfig, ChecklistBlockConfig, EvidenceBlockConfig, RuleBlockConfig } from '../../../types/card';
import type { BlueprintChecklistItem, BlueprintFormField, BlueprintExitOptions } from '../../../types/processBlueprint';

// ============================================================================
// TIPOS DE RESULTADO
// ============================================================================

export interface ValidationError {
  type: 'required_field' | 'checklist_incomplete' | 'attachment_required' | 'rule_blocked' | 'validation_failed' | 'justification_required';
  fieldId?: string;
  message: string;
}

export interface ValidationResult {
  valid: boolean;
  errors: ValidationError[];
}

// ============================================================================
// VALIDAÇÃO DE CONCLUSÃO
// ============================================================================

/**
 * Valida se uma tarefa pode ser concluída.
 * Verifica campos obrigatórios, checklist, anexos, regras e evidências.
 */
export function validateTaskCompletion(params: {
  formFields: BlueprintFormField[];
  formData: Record<string, unknown>;
  checklist: BlueprintChecklistItem[];
  checklistData: Record<string, boolean>;
  exitOptions: BlueprintExitOptions;
  notes: string;
  attachmentCount: number;
  executionConfig?: ExecutionConfig | null;
}): ValidationResult {
  const { formFields, formData, checklist, checklistData, exitOptions, notes, attachmentCount, executionConfig } = params;
  const errors: ValidationError[] = [];

  // 1. Campos obrigatórios
  for (const field of formFields) {
    if (!field.required) continue;
    const value = formData[field.id];
    if (value === undefined || value === null || value === '') {
      errors.push({
        type: 'required_field',
        fieldId: field.id,
        message: `Campo "${field.label}" é obrigatório`,
      });
    }
  }

  // 2. Validação de campos (min/max/pattern) — via executionConfig
  if (executionConfig) {
    for (const field of executionConfig.fields) {
      const value = formData[field.id];
      if (value === undefined || value === null || value === '') continue;
      if (field.validation) {
        const numVal = typeof value === 'number' ? value : parseFloat(String(value));
        if (field.validation.min !== undefined && !isNaN(numVal) && numVal < field.validation.min) {
          errors.push({
            type: 'validation_failed',
            fieldId: field.id,
            message: `"${field.label}" deve ser no mínimo ${field.validation.min}`,
          });
        }
        if (field.validation.max !== undefined && !isNaN(numVal) && numVal > field.validation.max) {
          errors.push({
            type: 'validation_failed',
            fieldId: field.id,
            message: `"${field.label}" deve ser no máximo ${field.validation.max}`,
          });
        }
        if (field.validation.pattern) {
          try {
            const regex = new RegExp(field.validation.pattern);
            if (!regex.test(String(value))) {
              errors.push({
                type: 'validation_failed',
                fieldId: field.id,
                message: field.validation.patternMessage ?? `"${field.label}" não corresponde ao formato esperado`,
              });
            }
          } catch {
            // Invalid regex — skip validation
          }
        }
      }
    }
  }

  // 3. Checklist obrigatório
  const requireAll = executionConfig?.requireAllChecklist ?? true;
  for (const item of checklist) {
    if (!requireAll && !item.required) continue;
    if (item.required && !checklistData[item.id]) {
      errors.push({
        type: 'checklist_incomplete',
        fieldId: item.id,
        message: `Item de checklist obrigatório: "${item.label}"`,
      });
    }
  }

  // 4. Anexo obrigatório
  if (exitOptions.requireAttachment && attachmentCount === 0) {
    errors.push({
      type: 'attachment_required',
      message: 'É necessário anexar pelo menos um arquivo',
    });
  }

  // 5. Justificativa obrigatória
  if (exitOptions.requireJustification && !notes.trim()) {
    errors.push({
      type: 'justification_required',
      message: 'Observação/justificativa é obrigatória',
    });
  }

  // 6. Regras customizadas
  if (executionConfig?.rules) {
    for (const rule of executionConfig.rules) {
      const result = evaluateRule(rule, formData);
      if (result.triggered) {
        if (rule.action === 'block_completion') {
          errors.push({
            type: 'rule_blocked',
            message: rule.message ?? 'Regra de bloqueio ativa',
          });
        }
        if (rule.action === 'require_attachment' && attachmentCount === 0) {
          errors.push({
            type: 'attachment_required',
            message: rule.message ?? 'Regra exige anexo obrigatório',
          });
        }
        if (rule.action === 'require_field') {
          // The field specified in the rule message/condition
          errors.push({
            type: 'required_field',
            message: rule.message ?? 'Regra exige preenchimento de campo',
          });
        }
      }
    }
  }

  return { valid: errors.length === 0, errors };
}

// ============================================================================
// AVALIAÇÃO DE REGRAS
// ============================================================================

interface RuleResult {
  triggered: boolean;
  message?: string;
}

/**
 * Avalia uma regra condicional contra os dados do formulário.
 */
export function evaluateRule(rule: StageRule, formData: Record<string, unknown>): RuleResult {
  const { condition } = rule;
  const fieldValue = formData[condition.fieldId];

  // is_empty / not_empty must evaluate before the null guard
  if (condition.operator === 'is_empty') {
    return { triggered: fieldValue === undefined || fieldValue === null || fieldValue === '', message: rule.message };
  }
  if (condition.operator === 'not_empty') {
    return { triggered: fieldValue !== undefined && fieldValue !== null && fieldValue !== '', message: rule.message };
  }

  if (fieldValue === undefined || fieldValue === null) {
    return { triggered: false };
  }

  const numFieldValue = typeof fieldValue === 'number' ? fieldValue : parseFloat(String(fieldValue));
  const numCondValue = typeof condition.value === 'number' ? condition.value : parseFloat(String(condition.value));

  let triggered = false;

  switch (condition.operator) {
    case 'equals':
      triggered = String(fieldValue) === String(condition.value);
      break;
    case 'not_equals':
      triggered = String(fieldValue) !== String(condition.value);
      break;
    case 'greater_than':
      triggered = !isNaN(numFieldValue) && !isNaN(numCondValue) && numFieldValue > numCondValue;
      break;
    case 'less_than':
      triggered = !isNaN(numFieldValue) && !isNaN(numCondValue) && numFieldValue < numCondValue;
      break;
    case 'greater_equal':
      triggered = !isNaN(numFieldValue) && !isNaN(numCondValue) && numFieldValue >= numCondValue;
      break;
    case 'less_equal':
      triggered = !isNaN(numFieldValue) && !isNaN(numCondValue) && numFieldValue <= numCondValue;
      break;
    case 'contains':
      triggered = String(fieldValue ?? '').includes(String(condition.value ?? ''));
      break;
  }

  return { triggered, message: rule.message };
}

// ============================================================================
// VISIBILIDADE DE CAMPOS
// ============================================================================

/**
 * Filtra campos visíveis com base em regras de visibilidade condicional.
 */
export function getVisibleFields(
  fields: ExecutionField[],
  formData: Record<string, unknown>,
): ExecutionField[] {
  return fields.filter((field) => {
    if (!field.visibilityRule) return true;
    return evaluateVisibility(field.visibilityRule, formData);
  });
}

/**
 * Avalia uma regra de visibilidade.
 */
function evaluateVisibility(rule: FieldVisibilityRule, formData: Record<string, unknown>): boolean {
  const value = formData[rule.dependsOnFieldId];

  switch (rule.operator) {
    case 'equals':
      return String(value) === String(rule.value);
    case 'not_equals':
      return String(value) !== String(rule.value);
    case 'greater_than': {
      const n = parseFloat(String(value));
      const r = parseFloat(String(rule.value));
      return !isNaN(n) && !isNaN(r) && n > r;
    }
    case 'less_than': {
      const n = parseFloat(String(value));
      const r = parseFloat(String(rule.value));
      return !isNaN(n) && !isNaN(r) && n < r;
    }
    case 'greater_equal': {
      const n = parseFloat(String(value));
      const r = parseFloat(String(rule.value));
      return !isNaN(n) && !isNaN(r) && n >= r;
    }
    case 'less_equal': {
      const n = parseFloat(String(value));
      const r = parseFloat(String(rule.value));
      return !isNaN(n) && !isNaN(r) && n <= r;
    }
    case 'contains':
      return String(value ?? '').includes(String(rule.value ?? ''));
    case 'is_empty':
      return value === undefined || value === null || value === '';
    case 'not_empty':
      return value !== undefined && value !== null && value !== '';
    default:
      return true;
  }
}

// ============================================================================
// AVALIAÇÃO DE REGRAS DE ESTÁGIO (batch)
// ============================================================================

export interface RuleEvaluation {
  rule: StageRule;
  triggered: boolean;
  message?: string;
}

/**
 * Avalia todas as regras de um estágio e retorna quais foram ativadas.
 */
export function evaluateAllRules(
  rules: StageRule[],
  formData: Record<string, unknown>,
): RuleEvaluation[] {
  return rules.map((rule) => {
    const result = evaluateRule(rule, formData);
    return { rule, triggered: result.triggered, message: result.message };
  });
}

/**
 * Verifica se alguma regra ativa bloqueia a conclusão.
 */
export function hasBlockingRules(
  rules: StageRule[],
  formData: Record<string, unknown>,
): boolean {
  return rules.some((rule) => {
    if (rule.action !== 'block_completion') return false;
    return evaluateRule(rule, formData).triggered;
  });
}

/**
 * Retorna warnings ativos (regras show_warning que foram ativadas).
 */
export function getActiveWarnings(
  rules: StageRule[],
  formData: Record<string, unknown>,
): string[] {
  return rules
    .filter((rule) => rule.action === 'show_warning' && evaluateRule(rule, formData).triggered)
    .map((rule) => rule.message ?? 'Atenção: condição de alerta ativa');
}

// ============================================================================
// BLOCK-BASED VALIDATION (V1)
// ============================================================================

/**
 * Validates block-based execution model for task completion.
 * Walks the block tree and checks: required fields, checklist completion,
 * evidence requirements, and conditional rules.
 */
export function validateBlocks(
  blocks: ExecutionBlock[],
  responses: Record<string, unknown>,
): ValidationResult {
  const errors: ValidationError[] = [];

  function walkBlocks(list: ExecutionBlock[]) {
    for (const block of list) {
      switch (block.type) {
        case 'group':
          if (block.children) walkBlocks(block.children);
          break;

        case 'field': {
          const cfg = block.config as FieldBlockConfig;
          const val = responses[block.id];
          if (cfg.required && (val === undefined || val === null || val === '')) {
            errors.push({ type: 'required_field', fieldId: block.id, message: `Campo obrigatório: ${block.label}` });
          }
          if (val != null && val !== '' && cfg.validation) {
            if (cfg.validation.min != null && Number(val) < cfg.validation.min) {
              errors.push({ type: 'validation_failed', fieldId: block.id, message: `${block.label}: valor mínimo é ${cfg.validation.min}` });
            }
            if (cfg.validation.max != null && Number(val) > cfg.validation.max) {
              errors.push({ type: 'validation_failed', fieldId: block.id, message: `${block.label}: valor máximo é ${cfg.validation.max}` });
            }
            if (cfg.validation.pattern && typeof val === 'string' && !new RegExp(cfg.validation.pattern).test(val)) {
              errors.push({ type: 'validation_failed', fieldId: block.id, message: `${block.label}: formato inválido` });
            }
          }
          break;
        }

        case 'checklist': {
          const cfg = block.config as ChecklistBlockConfig;
          const checked = (responses[block.id] as string[] | undefined) ?? [];
          const requiredItems = cfg.requireAll ? cfg.items : cfg.items.filter(i => i.required);
          const missing = requiredItems.filter(i => !checked.includes(i.id));
          if (missing.length > 0) {
            errors.push({ type: 'checklist_incomplete', fieldId: block.id, message: `Checklist "${block.label}": ${missing.length} item(ns) pendente(s)` });
          }
          break;
        }

        case 'evidence': {
          const cfg = block.config as EvidenceBlockConfig;
          const evData = responses[block.id] as Record<string, unknown> | undefined;
          if (cfg.requireAttachment && !evData?.attachment) {
            errors.push({ type: 'attachment_required', fieldId: block.id, message: `Anexo obrigatório: ${block.label}` });
          }
          if (cfg.requireJustification && !evData?.justification) {
            errors.push({ type: 'justification_required', fieldId: block.id, message: `Justificativa obrigatória: ${block.label}` });
          }
          break;
        }

        case 'rule': {
          const cfg = block.config as RuleBlockConfig;
          if (cfg.action === 'block_completion') {
            const fieldVal = responses[cfg.condition.fieldBlockId];
            if (evaluateBlockRule(cfg.condition.operator, fieldVal, cfg.condition.value)) {
              errors.push({ type: 'rule_blocked', fieldId: block.id, message: cfg.message || 'Regra de bloqueio ativa' });
            }
          }
          break;
        }
      }
    }
  }

  walkBlocks(blocks);
  return { valid: errors.length === 0, errors };
}

function evaluateBlockRule(
  operator: string,
  fieldValue: unknown,
  ruleValue?: string | number | boolean,
): boolean {
  const a = fieldValue;
  const b = ruleValue;
  switch (operator) {
    case 'equals': return a == b;
    case 'not_equals': return a != b;
    case 'greater_than': return Number(a) > Number(b);
    case 'less_than': return Number(a) < Number(b);
    case 'greater_equal': return Number(a) >= Number(b);
    case 'less_equal': return Number(a) <= Number(b);
    case 'contains': return typeof a === 'string' && typeof b === 'string' && a.includes(b);
    case 'is_empty': return a === undefined || a === null || a === '';
    case 'not_empty': return a !== undefined && a !== null && a !== '';
    default: return false;
  }
}
